/**
 * The installer sections of the property page, as in the report (10-serverless/120-pack/
 * installer_pages.py): business rates and tax, a typical day, supply and export, roof life and fire,
 * when and how to install, how they could pay, and the first-meeting checklist. Each shows everything
 * its block holds (nothing cut to fit); a missing block shows nothing.
 */

import { ChartArea, ClipboardList, Flame, HardHat, PlugZap, Receipt, Wallet } from "lucide-react";
import type { OwnedBuildingDetail } from "../api";
import type { TypicalDayBlock } from "../installer";
import { SectionHead } from "./PackSections";

const money = (v: number | null | undefined) => (typeof v === "number" ? `£${Math.round(v).toLocaleString("en-GB")}` : "—");
const years = (v: number | null | undefined) => (typeof v === "number" ? `${v} years` : "—");
const pct = (v: number | null | undefined) => (typeof v === "number" ? `${v}%` : "—");
const kwh = (v: number | null | undefined) => `${Math.round(v ?? 0).toLocaleString("en-GB")} kWh`;

const FIT: Record<string, string> = { good: "Good fit", possible: "Possible", poor: "Poor fit" };
const VERDICT: Record<string, string> = { ok: "Should outlast the system", check: "Check at survey", likely: "Re-roof likely first" };

/** Small print under a section */
function Notes({ notes }: { notes?: string[] }) {
  if (!notes?.length) return null;
  return (
    <div className="in-notes">
      {notes.map((n) => (
        <p key={n} className="muted">
          {n}
        </p>
      ))}
    </div>
  );
}

/** One day's chart: demand (navy line) against solar, split into used on site and exported */
function DayChart({ d, times, ymax }: { d: TypicalDayBlock["days"][number]; times: string[]; ymax: number }) {
  const W = 340, H = 150, L = 34, B = 20, T = 8, R = 6;
  const n = Math.max(d.demandKw.length, d.solarKw.length);
  if (n < 2) return null;
  const x = (i: number) => L + ((W - L - R) * i) / (n - 1);
  const y = (v: number) => T + (H - T - B) * (1 - v / ymax);
  const used = d.solarKw.map((s, i) => Math.min(s, d.demandKw[i] ?? 0));
  const area = (vals: number[]) => `M${x(0)},${y(0)}` + vals.map((v, i) => `L${x(i)},${y(v)}`).join("") + `L${x(n - 1)},${y(0)}Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={d.label}>
      {[0, 1, 2, 3].map((k) => {
        const v = (ymax * k) / 3;
        return (
          <g key={k}>
            <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="#e5e7eb" />
            <text x={L - 4} y={y(v) + 3} fontSize="8" fill="#4b5563" textAnchor="end">
              {Math.round(v).toLocaleString("en-GB")}
            </text>
          </g>
        );
      })}
      <text x={L - 4} y={T - 1} fontSize="7.5" fill="#4b5563" textAnchor="end">
        kW
      </text>
      <path d={area(d.solarKw)} fill="#e1ee2b" fillOpacity={0.55} />
      <path d={area(used)} fill="#C7D400" />
      <polyline fill="none" stroke="#202937" strokeWidth={1.6} points={d.demandKw.map((v, i) => `${x(i)},${y(v)}`).join(" ")} />
      {times.map((t, i) =>
        t.endsWith(":00") && Number(t.slice(0, 2)) % 6 === 0 ? (
          <text key={t} x={x(i)} y={H - 6} fontSize="8" fill="#4b5563" textAnchor="middle">
            {t}
          </text>
        ) : null,
      )}
    </svg>
  );
}

/** Business rates and tax: after the return on investment */
export function TaxSection({ b }: { b: OwnedBuildingDetail }) {
  const tx = b.installer?.tax;
  if (!tx) return null;
  const ca = tx.capitalAllowances;
  return (
    <section className="card eo" aria-labelledby="in-tax">
      <SectionHead id="in-tax" kicker="Section 9 continued" icon={Receipt} title="Business rates and tax"
        sub="Two things that change what the system really costs the owner: whether it adds to the business rates bill, and whether the owner can claim tax relief on it." />
      <div className="in-grid">
        <div className="pk-panel">
          <h3 className="pk-sub">Business rates{tx.nation ? ` in ${tx.nation}` : ""}</h3>
          <p>{tx.businessRates.note}</p>
        </div>
        <div className="pk-panel">
          <h3 className="pk-sub">Capital allowances{ca.ownerType ? ` (${ca.ownerType})` : ""}</h3>
          {ca.firstYearSaving != null && (
            <dl className="building-facts">
              <div><dt>First-year tax saving (indicative)</dt><dd><b>{money(ca.firstYearSaving)}</b> at {pct(ca.rateUsed)}</dd></div>
              {ca.firstYearSavingIfAiaUsed != null && <div><dt>If the Annual Investment Allowance is already used</dt><dd>{money(ca.firstYearSavingIfAiaUsed)}</dd></div>}
              {ca.netCostAfterRelief != null && <div><dt>Cost after first-year relief</dt><dd>{money(ca.netCostAfterRelief)}</dd></div>}
              <div><dt>Payback before tax</dt><dd>{years(ca.beforeTaxPayback)}</dd></div>
              <div><dt>Payback after tax</dt><dd>{years(ca.afterTaxPayback)}</dd></div>
            </dl>
          )}
          <ul className="site-list">{ca.notes.map((n) => <li key={n}>{n}</li>)}</ul>
        </div>
      </div>
      <Notes notes={tx.notes} />
    </section>
  );
}

/** A typical day: four small charts */
export function TypicalDaySection({ b }: { b: OwnedBuildingDetail }) {
  const td = b.installer?.typicalDay;
  if (!td?.days.length) return null;
  const ymax = Math.max(1, ...td.days.flatMap((d) => [...d.demandKw, ...d.solarKw])) * 1.1;
  return (
    <section className="card eo" aria-labelledby="in-day">
      <SectionHead id="in-day" kicker="Section 9 continued" icon={ChartArea} title="A typical day"
        sub="How the building's use and the panels' output line up over a day, in summer and winter. Where the panels make more than the building uses, the rest is exported, or could charge a battery." />
      <div className="in-grid">
        {td.days.map((d) => (
          <figure key={d.label} className="in-day">
            <h3 className="pk-sub">{d.label}</h3>
            <DayChart d={d} times={td.times} ymax={ymax} />
            <figcaption>
              <p>{d.summary}</p>
              <p className="muted">
                Sunrise {d.sunrise}, sunset {d.sunset}; {d.openHours ? `open about ${d.openHours[0]} to ${d.openHours[1]}` : "closed"}. Uses {kwh(d.demandKwh)}; panels make {kwh(d.solarKwh)}; {kwh(d.usedOnSiteKwh)} used on site,{" "}
                {kwh(d.exportKwh)} exported, {kwh(d.importKwh)} still bought. Solar covers {pct(d.solarCoversPct)} of the day's use.
              </p>
            </figcaption>
          </figure>
        ))}
      </div>
      <p className="in-legend">
        <span><i style={{ background: "#202937", height: 3 }} />Building's use</span>
        <span><i style={{ background: "#C7D400" }} />Solar used on site</span>
        <span><i style={{ background: "#e1ee2b", opacity: 0.55 }} />Solar exported</span>
      </p>
      <Notes notes={td.notes} />
    </section>
  );
}

/** Supply, connection and export, with the export-limited option where the grid is tight */
export function SupplySection({ b }: { b: OwnedBuildingDetail }) {
  const sp = b.installer?.supply;
  if (!sp) return null;
  const lim = sp.exportLimited;
  const rows = lim
    ? [
        { label: "Recommended, exporting freely", r: { ...lim.recommended, curtailedKwh: 0 } },
        ...(lim.zeroExport?.best ? [{ label: "Best size with no export", r: lim.zeroExport.best }] : []),
        ...(lim.capped?.best ? [{ label: `Best size with export capped at ${lim.capped.capKw} kW`, r: lim.capped.best }] : []),
      ]
    : [];
  return (
    <section className="card eo" aria-labelledby="in-supply">
      <SectionHead id="in-supply" kicker="Section 9 continued" icon={PlugZap} title="Supply, connection and export"
        sub="What the building's electricity supply is likely to be, how the system would connect to the grid, and whether the local network can take its export." />
      {/* The headline figures, as in the report */}
      <div className="eo-stats">
        <div><b>{sp.peakKw ? `~${sp.peakKw.toLocaleString("en-GB")} kW` : "—"}</b><span>estimated peak demand</span></div>
        <div><b>{sp.supply.label || "—"}</b><span>likely supply</span></div>
        <div><b>{sp.connection?.rule || "—"}</b><span>grid connection rule</span></div>
        <div className={sp.export?.constrained ? "is-dark" : undefined}><b>{sp.export?.peakKw ? `~${sp.export.peakKw.toLocaleString("en-GB")} kW` : "—"}</b><span>estimated peak export</span></div>
      </div>
      <div className="in-grid">
        <div className="pk-panel">
          <h3 className="pk-sub">The building's supply</h3>
          <ul className="site-list">
            <li>{sp.peakNote}</li>
            <li>{sp.supply.note}</li>
            <li>{sp.halfHourly.note}</li>
            <li><b>{sp.halfHourly.ask}</b></li>
          </ul>
        </div>
        {sp.connection && (
          <div className="pk-panel">
            <h3 className="pk-sub">Connecting to the grid ({sp.connection.rule})</h3>
            <p>{sp.connection.note}</p>
            <ol className="in-steps">{sp.connection.steps.map((s) => <li key={s}>{s}</li>)}</ol>
            <p className="muted">{sp.connection.timescale}</p>
          </div>
        )}
      </div>
      {sp.export && (
        <div className="pk-panel">
          <h3 className="pk-sub">Export and the local network</h3>
          <p>{sp.export.note}</p>
          <ul className="site-list">{sp.notes.map((n) => <li key={n} className="is-warn">{n}</li>)}</ul>
        </div>
      )}
      {lim && (
        <div className="pk-panel">
          <h3 className="pk-sub">If the network limits export</h3>
          <p>{lim.scheme}</p>
          <div className="in-table">
            <table>
              <thead><tr><th>Option</th><th>Size</th><th>Installed</th><th>Payback</th><th>Return a year</th><th>25-year value today</th><th>Turned down a year</th></tr></thead>
              <tbody>
                {rows.map(({ label, r }) => (
                  <tr key={label}><td>{label}</td><td>{r.kwp} kWp</td><td>{money(r.capex)}</td><td>{years(r.payback)}</td><td>{pct(r.irr)}</td><td>{money(r.npv)}</td><td>{r.curtailedKwh ? kwh(r.curtailedKwh) : "—"}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          {lim.zeroExport && <p>{lim.zeroExport.summary}</p>}
          {lim.capped && <p>{lim.capped.summary}</p>}
          <p className="muted">{lim.note}</p>
          {!!lim.zeroExport?.options.length && (
            <details>
              <summary>Every size with no export</summary>
              <div className="in-table">
                <table>
                  <thead><tr><th>Size</th><th>Installed</th><th>Used on site</th><th>Turned down a year</th><th>Payback</th><th>25-year value today</th></tr></thead>
                  <tbody>
                    {lim.zeroExport.options.map((o) => (
                      <tr key={o.kwp}><td>{o.kwp} kWp</td><td>{money(o.capex)}</td><td>{pct(o.selfUse)}</td><td>{kwh(o.curtailedKwh)}</td><td>{years(o.payback)}</td><td>{money(o.npv)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      )}
    </section>
  );
}

/** Roof life, fire and insurance: after the roof analysis */
export function RoofLifeSection({ b }: { b: OwnedBuildingDetail }) {
  const rl = b.installer?.roofLife;
  if (!rl) return null;
  const rem = rl.remainingYears;
  return (
    <section className="card eo" aria-labelledby="in-roof">
      <SectionHead id="in-roof" kicker="Section 10 continued" icon={Flame} title="Roof life, fire and insurance"
        sub={`Panels stay on a roof for ${rl.systemYears} years or more, so the roof under them needs to last as long. And insurers now expect commercial rooftop solar to be designed with fire safety in mind.`} />
      <dl className="building-facts">
        <div><dt>Roof age</dt><dd>{rl.roofAgeYears != null ? `About ${rl.roofAgeYears} years (estimated)` : "Not known"}</dd></div>
        <div><dt>Covering</dt><dd>{rl.covering ?? "Not known"}</dd></div>
        <div><dt>Typical life left</dt><dd>{rem.low != null ? `${rem.low} to ${rem.high} years` : "—"}</dd></div>
        <div><dt>Against the system</dt><dd><span className={`in-pill ${rl.verdict}`}>{VERDICT[rl.verdict]}</span></dd></div>
      </dl>
      <p className={`in-callout ${rl.verdict}`}>{rl.reroof.advice}{rl.coveringSeen ? ` What we saw of the roof: ${rl.coveringSeen}` : ""}</p>
      <div className="in-grid">
        <div className="pk-panel"><h3 className="pk-sub">Fire safety</h3><ul className="site-list">{rl.fire.map((x) => <li key={x}>{x}</li>)}</ul></div>
        <div className="pk-panel"><h3 className="pk-sub">The building's insurer</h3><ul className="site-list">{rl.insurer.map((x) => <li key={x}>{x}</li>)}</ul></div>
      </div>
      <Notes notes={rl.notes} />
    </section>
  );
}

/** When and how to install */
export function InstallSection({ b }: { b: OwnedBuildingDetail }) {
  const ip = b.installer?.installPlan;
  if (!ip) return null;
  return (
    <section className="card eo" aria-labelledby="in-install">
      <SectionHead id="in-install" kicker="Section 10 continued" icon={HardHat} title="When and how to install"
        sub="A first view of the installation: when to do it around the way the building is used, how long it could take, and how to work safely at height." />
      <dl className="building-facts">
        <div><dt>Working days</dt><dd>{ip.durationDays ? `${ip.durationDays.low} to ${ip.durationDays.high} (estimated)` : "—"}</dd></div>
        {ip.access.cost && <div><dt>{ip.access.cost.label}</dt><dd>{money(ip.access.cost.amount)} (your pricing model)</dd></div>}
      </dl>
      <p className="in-callout ok"><b>Best time: {ip.window.best}.</b> {ip.window.why}</p>
      <div className="pk-panel"><h3 className="pk-sub">Getting onto the roof</h3><p><b>{ip.access.method}.</b> {ip.access.why}</p></div>
      <Notes notes={ip.notes} />
    </section>
  );
}

/** How they could pay for it: after grants */
export function FundingRouteSection({ b }: { b: OwnedBuildingDetail }) {
  const fr = b.installer?.fundingRoute;
  if (!fr?.routes.length) return null;
  const routes = [...fr.routes].sort((a, c) => Number(a.route !== fr.recommended) - Number(c.route !== fr.recommended));
  return (
    <section className="card eo" aria-labelledby="in-pay">
      <SectionHead id="in-pay" kicker="Section 12 continued" icon={Wallet} title="How they could pay for it"
        sub={<>The ways this owner could pay for the system, and how well each fits what we know about them.{fr.recommended && <> Suggested: <b>{fr.recommended}</b>.</>}</>} />
      <div className="in-grid">
        {routes.map((r) => (
          <div key={r.route} className="pk-panel">
            <h3 className="pk-sub">
              {r.route} <span className={`in-pill ${r.fit}`}>{FIT[r.fit]}</span>
              {r.route === fr.recommended && <span className="in-pill info">Suggested</span>}
            </h3>
            <p>{r.what}</p>
            <ul className="site-list">{r.why.map((w) => <li key={w}>{w}</li>)}</ul>
          </div>
        ))}
      </div>
      <Notes notes={fr.notes} />
    </section>
  );
}

/** Your first meeting: questions and documents, worked out for this property */
export function FirstMeetingSection({ b }: { b: OwnedBuildingDetail }) {
  const fm = b.installer?.firstMeeting;
  if (!fm?.sections.length) return null;
  const SOURCE: Record<string, string> = { flag: "Before you start", gap: "Not found in our research", standard: "Standard" };
  return (
    <section className="card eo" aria-labelledby="in-meet">
      <SectionHead id="in-meet" kicker="Section 12 continued" icon={ClipboardList} title="Your first meeting"
        sub="What to ask and what to collect at the first meeting, worked out for this property: what we found before you start, what our research couldn't find, and the standard questions for a commercial job. Where we found something, it's shown to confirm." />
      {fm.sections.map((sec) => (
        <div key={sec.title} className="pk-panel">
          <h3 className="pk-sub">{sec.title}</h3>
          <ul className="in-meet">
            {sec.items.map((it) => (
              <li key={it.key}>
                <span className={`in-pill ${it.priority}`}>{it.priority === "must" ? "Must" : "Should"}</span>
                <div>
                  <b>{it.question}</b>
                  {it.found && <p className="in-found">{it.answered ? "✓ " : ""}{it.found}</p>}
                  {/* What our research couldn't settle on this point, in its own words, to ask about */}
                  {it.gaps?.map((g) => (
                    <p key={g} className="in-gap">
                      Still open: {g}
                    </p>
                  ))}
                  <p className="muted">{it.why} · {SOURCE[it.source]}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {!!fm.documents.length && (
        <div className="pk-panel">
          <h3 className="pk-sub">Documents to ask for</h3>
          <ul className="in-docs">{fm.documents.map((d) => <li key={d}>{d}</li>)}</ul>
        </div>
      )}
    </section>
  );
}
